import { authContext } from '../auth-context.ts';

// tio-data deliberately doesn't depend on tio-common, so inline the
// same defaults the auth handler uses for the builder pipeline.
const defaultTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || '';
const defaultRequestId = () => crypto.randomUUID();

/**
 * Returns the TIO auth header bundle for a *direct* `fetch()` call —
 * the same headers `TioAuthRequestHandler` injects into builder
 * requests. Use this when a caller can't go through `store.request()`:
 * binary downloads (Blob/ZIP responses that bypass JSON parsing) and
 * presigned-S3 uploads composed off a TIO presigned-URL response.
 *
 * Builders should NOT use this — they get the same headers
 * automatically via the auth handler. This helper exists specifically
 * to let those few direct-fetch callers stop reading
 * `adapter.headers`, which is the last hook keeping otherwise-empty
 * per-model adapter files alive.
 *
 * Owner-free (ADR-001): the auth token / x-auth-key come from
 * `tio-data/auth-context` at call time, so this is callable from
 * anywhere — components, plain modules, authenticators.
 */
export interface TioAuthHeadersOptions {
  apiKey: string;
  contentType?: string;
  accept?: string;
  jsonApiKeyFormat?: string;
  timeZone?: () => string;
  requestId?: () => string;
}

export function buildTioAuthHeaders(options: TioAuthHeadersOptions): Headers {
  const headers = new Headers();
  if (options.contentType) headers.set('Content-Type', options.contentType);
  if (options.accept) headers.set('Accept', options.accept);
  headers.set('x-api-key', options.apiKey);
  const authKey = authContext.publicKey();
  if (authKey) headers.set('x-auth-key', authKey);
  if (options.jsonApiKeyFormat) headers.set('x-json-api-key-format', options.jsonApiKeyFormat);
  headers.set('Time-Zone', (options.timeZone ?? defaultTimeZone)());
  headers.set('X-Request-Id', (options.requestId ?? defaultRequestId)());

  // Mirror the auth handler: any real token is sent (the post-login boot
  // holds the token before `isAuthenticated` flips true — see
  // request-manager.ts), garbage/absent tokens are not.
  const accessToken = authContext.token();
  if (accessToken) {
    headers.set('tio-auth-token', accessToken);
  }

  return headers;
}

/**
 * Same auth-header bundle as {@link buildTioAuthHeaders}, but as a plain
 * `Record<string, string>` instead of a `Headers` object.
 *
 * Some consumers can't take a `Headers` object — notably tio-ui's
 * `<DownloadButton @authHeaders={{...}} />`, whose `authHeaders` arg is typed
 * `() => Record<string, string>` (see `addons/tio-ui/src/components/download-button.gts`).
 * The flagged admin call sites that fed it `adapterFor('application').headers`
 * (a plain Record) — e.g. `accounting/invoices/show.gts#downloadHeaders` — swap to
 * this helper in 3.2c-ii. (The `company-settings/data-transfer-agent.gts` +
 * `profile/payments.gts` call sites already do `Object.fromEntries(
 * buildTioAuthHeaders(...).entries())` inline; this formalizes that one-liner.)
 *
 * It delegates to {@link buildTioAuthHeaders} and flattens the result, so the
 * key set + values are GUARANTEED identical (incl. the vnd.api+json
 * Content-Type / Accept when those options are supplied). Note: `Headers`
 * lowercases its keys, so the emitted Record has lowercase keys (`content-type`,
 * `x-api-key`, etc.) — exactly what the inline `Object.fromEntries(...entries())`
 * call sites already produce.
 */
export function buildTioAuthHeadersRecord(options: TioAuthHeadersOptions): Record<string, string> {
  return Object.fromEntries(buildTioAuthHeaders(options).entries());
}
